import React, { useState } from 'react';
import { Box, IconButton, Popover, Typography } from '@mui/material';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { glassSx, mvkgTokens } from '../../theme/mvkgTokens';
import Kicker from '../ui/Kicker';

/**
 * Notifications bell + dropdown. Data-driven: pass `items` (array). When empty,
 * shows a clean "all caught up" state and no pulse dot. There is currently no
 * notifications backend, so callers may simply render <NotificationDropdown />
 * (empty) until one exists — no fabricated alerts.
 */
export default function NotificationDropdown({ items = [] }) {
  const [anchor, setAnchor] = useState(null);
  const open = Boolean(anchor);
  const hasItems = items.length > 0;

  return (
    <>
      <IconButton
        color="inherit"
        aria-label="Notifications"
        onClick={(e) => setAnchor(e.currentTarget)}
        sx={{ position: 'relative', color: 'text.secondary' }}
      >
        <NotificationsNoneIcon />
        {hasItems && (
          <Box
            className="status-pulse"
            sx={{
              position: 'absolute',
              top: 8,
              right: 9,
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: 'error.main',
              border: '2px solid #fff',
            }}
          />
        )}
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: 360,
              maxHeight: 420,
              borderRadius: `${mvkgTokens.radius.lg}px`,
              ...glassSx(true),
              overflow: 'hidden',
            },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${mvkgTokens.surface.glassBorder}` }}>
          <Typography sx={{ fontWeight: 700 }}>Inbox</Typography>
          <Kicker>{hasItems ? `${items.length} new` : 'No new alerts'}</Kicker>
        </Box>

        {hasItems ? (
          <Box sx={{ p: 1, maxHeight: 340, overflow: 'auto' }}>
            {items.map((n, i) => (
              <Box
                key={n.id || i}
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  px: 1.5,
                  py: 1.25,
                  borderRadius: 2,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: mvkgTokens.surface.glassSoft },
                }}
              >
                <Box sx={{ mt: 0.75, width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>{n.text}</Typography>
                  {n.at && (
                    <Typography variant="caption" sx={{ fontFamily: mvkgTokens.font.mono, color: 'text.secondary' }}>
                      {n.at}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        ) : (
          <Box sx={{ px: 3, py: 4, textAlign: 'center', color: 'text.secondary' }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 36, opacity: 0.4, mb: 1 }} />
            <Typography variant="body2">You're all caught up.</Typography>
          </Box>
        )}
      </Popover>
    </>
  );
}
